import { CSSProperties, useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'

import { MagnifyingGlassPlus } from '@phosphor-icons/react'

export interface DeliveryPhoto {
  url: string
}

interface DeliveryPhotoCarouselProps {
  photos: DeliveryPhoto[]
  onPhotoPress: (url: string) => void
}

const DESKTOP_BREAKPOINT_PX = 500
const DESKTOP_PHOTO_HEIGHT_MAX = 360 // upper bound; actual height is computed to fit all photos in one row
const DESKTOP_PHOTO_HEIGHT_MIN = 200 // lower bound to keep photos readable on narrow desktops
const DESKTOP_GAP_PX = 12
const SINGLE_PHOTO_MAX_HEIGHT_PX = 300 // cap single-photo size; comparable to the current 255×255 footprint without cropping
const TWO_PHOTO_MAX_HEIGHT_PX = 220 // 2-photo case is centered; cap so they read as "two thumbnails" not "two heroes"
const MOBILE_CARD_WIDTH_PCT = 0.43 // 43% so 2 fit + a sliver of the 3rd peeks (no scroller padding)
const MOBILE_GAP_PX = 8
const FADE_BG_COLOR = 'rgba(247, 245, 240, 1)' // matches page bg
const FADE_BG_TRANSPARENT = 'rgba(247, 245, 240, 0)'

/**
 * DeliveryPhotoCarousel component displays a carousel of delivery photos.
 * @param props - The component props.
 * @param props.photos - An array of delivery photos, each containing a URL.
 * @param props.onPhotoPress - A callback function that is triggered when a photo is pressed, receiving the photo URL as an argument.
 * @returns JSX.Element representing the delivery photo carousel.
 */
export default function DeliveryPhotoCarousel({ photos, onPhotoPress }: DeliveryPhotoCarouselProps) {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [activeIndex, setActiveIndex] = useState(0)
  // Per-photo aspect ratio (width / height) captured on image load. Used on desktop to
  // compute a unified row height so all photos fit in one row at their natural ratio.
  const [aspectRatios, setAspectRatios] = useState<Record<number, number>>({})

  // Track parent container width — drives mobile vs desktop layout.
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? 0
      setContainerWidth(w)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const isDesktop = containerWidth >= DESKTOP_BREAKPOINT_PX

  // Unified-row height: pick the largest height at which the sum of photo widths
  // (height × ratio) plus inter-photo gaps fits within the container. Falls back to MAX
  // while aspect ratios are still loading. The 2-photo case uses a tighter cap so the
  // pair reads as a centered thumbnail pair instead of stretching to fill the row.
  const allRatiosKnown = photos.every((_, idx) => aspectRatios[idx] !== undefined)
  const heightMax = photos.length === 2 ? TWO_PHOTO_MAX_HEIGHT_PX : DESKTOP_PHOTO_HEIGHT_MAX
  let unifiedRowHeight = heightMax
  if (allRatiosKnown && photos.length > 0) {
    const totalGap = (photos.length - 1) * DESKTOP_GAP_PX
    const sumRatios = photos.reduce((s, _, idx) => s + (aspectRatios[idx] ?? 1), 0)
    const fitHeight = Math.floor((containerWidth - totalGap) / sumRatios)
    unifiedRowHeight = Math.max(DESKTOP_PHOTO_HEIGHT_MIN, Math.min(heightMax, fitHeight))
  }

  const recordRatio = (idx: number) => (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    if (!img.naturalWidth || !img.naturalHeight) return
    const ratio = img.naturalWidth / img.naturalHeight
    setAspectRatios(prev => (prev[idx] === ratio ? prev : { ...prev, [idx]: ratio }))
  }

  // Active index tracking for mobile pagination dots.
  useEffect(() => {
    if (isDesktop) return
    const el = scrollerRef.current
    if (!el || photos.length < 2) return

    const handleScroll = () => {
      const cards = el.querySelectorAll<HTMLElement>('[data-carousel-card]')
      if (cards.length === 0) return
      const cardSlot = cards[0].getBoundingClientRect().width + MOBILE_GAP_PX
      const idx = Math.round(el.scrollLeft / cardSlot)
      setActiveIndex(Math.min(photos.length - 1, Math.max(0, idx)))
    }

    handleScroll()
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => el.removeEventListener('scroll', handleScroll)
  }, [photos.length, isDesktop])

  if (photos.length === 0) {
    return <div ref={rootRef} />
  }

  // Render is split into branches by length and layout, but the ROOT element stays the
  // same so ResizeObserver can keep tracking width across layout switches.
  let body: ReactElement

  const magnifierBadge = (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        top: 8,
        right: 8,
        width: 30,
        height: 30,
        borderRadius: 20,
        backgroundColor: '#000000',
        border: '1px solid #21211F',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <MagnifyingGlassPlus size={15} color="#FFFFFF" weight="bold" />
    </span>
  )

  const card = (photo: DeliveryPhoto, idx: number, sizingStyle: CSSProperties, imgStyle: CSSProperties) => (
    <button
      key={idx}
      data-carousel-card
      onClick={() => onPhotoPress(photo.url)}
      style={{
        ...sizingStyle,
        padding: 0,
        margin: 0,
        background: 'transparent',
        border: 0,
        cursor: 'pointer',
        scrollSnapAlign: 'start',
        position: 'relative',
        display: 'block',
      }}
      aria-label={`Open delivery photo ${idx + 1} of ${photos.length}`}
    >
      <img
        src={photo.url}
        alt={`Delivery photo ${idx + 1}`}
        onLoad={recordRatio(idx)}
        style={{
          display: 'block',
          borderRadius: 8,
          ...imgStyle,
        }}
      />
      {magnifierBadge}
    </button>
  )

  // Layout rules (per product spec): 1 or 2 photos center horizontally; 3+ left-align.
  const centerLayout = photos.length <= 2
  // Mobile peek-scroll only applies to 3+ photos. With 1 or 2 photos everything fits, so
  // we use the same unified-row layout the desktop branch uses (just centered).
  const useUnifiedRow = photos.length === 2 || (photos.length >= 3 && isDesktop)

  if (photos.length === 1) {
    // Single-photo case: capped height so it doesn't dominate the page. Centered horizontally.
    body = (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {card(
          photos[0],
          0,
          { display: 'inline-block' },
          { maxHeight: SINGLE_PHOTO_MAX_HEIGHT_PX, maxWidth: '100%', height: 'auto', width: 'auto' }
        )}
      </div>
    )
  } else if (useUnifiedRow) {
    // Unified-row: all photos at the same height (chosen so the combined natural widths fit
    // in the container). Centered for 2 photos, left-aligned for 3+.
    body = (
      <div
        style={{
          display: 'flex',
          gap: DESKTOP_GAP_PX,
          justifyContent: centerLayout ? 'center' : 'flex-start',
          alignItems: 'flex-start',
          flexWrap: 'nowrap',
        }}
      >
        {photos.map((photo, idx) =>
          card(
            photo,
            idx,
            { height: unifiedRowHeight, flex: '0 0 auto' },
            { height: '100%', width: 'auto', maxWidth: '100%' }
          )
        )}
      </div>
    )
  } else {
    // Mobile: scroll-snap carousel with 2 photos visible + a sliver of the 3rd peeking.
    const cardWidthPx = Math.max(120, Math.floor(containerWidth * MOBILE_CARD_WIDTH_PCT))
    body = (
      <div style={{ position: 'relative' }}>
        <div
          ref={scrollerRef}
          style={{
            display: 'flex',
            gap: MOBILE_GAP_PX,
            alignItems: 'flex-start',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
          aria-roledescription="carousel"
          aria-label="Delivery photos"
        >
          {photos.map((photo, idx) =>
            card(photo, idx, { flex: `0 0 ${cardWidthPx}px`, width: cardWidthPx }, { width: '100%', height: 'auto' })
          )}
        </div>
        {/* Right-edge fade hint */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 24,
            height: '100%',
            background: `linear-gradient(to left, ${FADE_BG_COLOR}, ${FADE_BG_TRANSPARENT})`,
            pointerEvents: 'none',
          }}
        />
        {/* Pagination dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 10 }} aria-hidden>
          {photos.map((_, idx) => (
            <span
              key={idx}
              style={{
                width: idx === activeIndex ? 18 : 6,
                height: 6,
                borderRadius: 3,
                background: idx === activeIndex ? '#21211F' : '#C3C3BE',
                transition: 'width 150ms ease, background 150ms ease',
              }}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div ref={rootRef} style={{ width: '100%', marginTop: 12 }}>
      {body}
    </div>
  )
}
